/**
 * 簽核流程工具函數
 * 提供向下相容轉換、資料驗證等功能
 * @since v2.0.0 - Multiple Approvers Support
 */

import { ApprovalStepDefinition, ApproverDefinition } from '../models/approval-flow-definition.model';

/**
 * 將舊版單一簽核人員格式轉換為新版多簽核人員格式
 * @param step 簽核步驟定義（可能是舊格式或新格式）
 * @returns 轉換後的簽核步驟（確保包含 approvers 陣列）
 */
export function convertLegacyStepToMultiApprover(
  step: ApprovalStepDefinition
): ApprovalStepDefinition {
  // 如果已經有 approvers 陣列且不為空，直接返回
  if (step.approvers && step.approvers.length > 0) {
    return {
      ...step,
      approvalLogic: step.approvalLogic || 'any_one' // 確保有預設值
    };
  }

  // 轉換舊格式到新格式
  const approvers: ApproverDefinition[] = [];

  // 檢查是否有舊版欄位
  if (step.approverType) {
    const approver: ApproverDefinition = {
      type: step.approverType
    };

    if (step.approverType === 'specific_user') {
      approver.userId = step.userId;
      approver.userName = step.userName;
    } else if (step.approverType === 'role_based') {
      approver.departmentId = step.departmentId;
      approver.departmentName = step.departmentName;
      approver.roleId = step.roleId;
      approver.roleName = step.roleName;
      approver.isDynamicDepartment = step.isDynamicDepartment;
    }

    approvers.push(approver);
  }

  return {
    ...step,
    approvers,
    approvalLogic: step.approvalLogic || 'any_one'
  };
}

/**
 * 批次轉換簽核步驟陣列
 * @param steps 簽核步驟陣列
 * @returns 轉換後的簽核步驟陣列
 */
export function convertLegacyStepsToMultiApprover(
  steps: ApprovalStepDefinition[]
): ApprovalStepDefinition[] {
  return steps.map(step => convertLegacyStepToMultiApprover(step));
}

/**
 * 檢查簽核步驟是否為舊格式（只有單一簽核人員）
 * @param step 簽核步驟定義
 * @returns true 表示是舊格式
 */
export function isLegacyStep(step: ApprovalStepDefinition): boolean {
  // 如果有 approvers 陣列，就不是舊格式
  if (step.approvers && step.approvers.length > 0) {
    return false;
  }

  // 如果有舊版的 approverType，就是舊格式
  return !!step.approverType;
}

/**
 * 驗證簽核人員定義是否有效
 * @param approver 簽核人員定義
 * @returns 驗證結果
 */
export function validateApprover(approver: ApproverDefinition): {
  isValid: boolean;
  errors: string[];
} {
  const errors: string[] = [];

  if (!approver.type) {
    errors.push('簽核人員類型不能為空');
  }

  if (approver.type === 'specific_user') {
    if (!approver.userId) {
      errors.push('指定人員時必須提供使用者 ID');
    }
  } else if (approver.type === 'role_based') {
    if (!approver.departmentId) {
      errors.push('角色簽核時必須提供部門 ID');
    }
    if (!approver.roleId) {
      errors.push('角色簽核時必須提供角色 ID');
    }
  }

  return {
    isValid: errors.length === 0,
    errors
  };
}

/**
 * 驗證簽核步驟定義是否有效
 * @param step 簽核步驟定義
 * @returns 驗證結果
 */
export function validateApprovalStep(step: ApprovalStepDefinition): {
  isValid: boolean;
  errors: string[];
} {
  const errors: string[] = [];

  if (!step.name || step.name.trim() === '') {
    errors.push('簽核步驟名稱不能為空');
  }

  if (!step.approvers || step.approvers.length === 0) {
    errors.push('至少需要一位簽核人員');
  } else {
    // 驗證每個簽核人員
    step.approvers.forEach((approver, index) => {
      const validation = validateApprover(approver);
      if (!validation.isValid) {
        validation.errors.forEach(error => {
          errors.push(`簽核人員 ${index + 1}: ${error}`);
        });
      }
    });
  }

  if (!step.approvalLogic) {
    errors.push('必須指定簽核邏輯（any_one 或 all）');
  }

  return {
    isValid: errors.length === 0,
    errors
  };
}

/**
 * 取得簽核人員的顯示名稱
 * @param approver 簽核人員定義
 * @returns 顯示名稱
 */
export function getApproverDisplayName(approver: ApproverDefinition): string {
  if (approver.type === 'specific_user') {
    return approver.userName || approver.userId || '未知使用者';
  } else if (approver.type === 'role_based') {
    const dept = approver.departmentName || approver.departmentId || '未知部門';
    const role = approver.roleName || approver.roleId || '未知角色';
    return `${dept} - ${role}`;
  }
  return '未知簽核人員';
}

/**
 * 取得簽核步驟的簽核人員摘要文字
 * @param step 簽核步驟定義
 * @returns 摘要文字，例如："張三、李四 (任一人通過)"
 */
export function getApprovalStepSummary(step: ApprovalStepDefinition): string {
  if (!step.approvers || step.approvers.length === 0) {
    return '未設定簽核人員';
  }

  const approverNames = step.approvers
    .map(approver => getApproverDisplayName(approver))
    .join('、');

  const logicText = step.approvalLogic === 'any_one' ? '任一人通過' : '所有人都要通過';

  return `${approverNames} (${logicText})`;
}

/**
 * 建立新的簽核步驟（使用預設值）
 * @param order 步驟順序
 * @returns 新的簽核步驟定義
 * @note name 屬性應由呼叫端使用 i18n 服務進行翻譯（key: 'form.approval.step_name'）
 */
export function createNewApprovalStep(order: number): ApprovalStepDefinition {
  return {
    id: generateStepId(),
    name: `Approval Step ${order}`, // 預設英文名稱，應由呼叫端使用 i18n 翻譯覆蓋
    order,
    approvers: [],
    approvalLogic: 'any_one',
    optional: false,
    requireComment: false,
    requireSignature: false,
    hasAdvancedSettings: false
  };
}

/**
 * 產生簽核步驟 ID
 * @returns UUID 格式的步驟 ID
 */
function generateStepId(): string {
  return 'step-' + Date.now() + '-' + Math.random().toString(36).substr(2, 9);
}

/**
 * 建立新的簽核人員定義（指定人員）
 * @param userId 使用者 ID
 * @param userName 使用者名稱
 * @returns 簽核人員定義
 */
export function createSpecificUserApprover(
  userId: string,
  userName: string
): ApproverDefinition {
  return {
    type: 'specific_user',
    userId,
    userName
  };
}

/**
 * 建立新的簽核人員定義（角色）
 * @param departmentId 部門 ID
 * @param departmentName 部門名稱
 * @param roleId 角色 ID
 * @param roleName 角色名稱
 * @param isDynamicDepartment 是否為動態部門
 * @returns 簽核人員定義
 */
export function createRoleBasedApprover(
  departmentId: string,
  departmentName: string,
  roleId: string,
  roleName: string,
  isDynamicDepartment: boolean = false
): ApproverDefinition {
  return {
    type: 'role_based',
    departmentId,
    departmentName,
    roleId,
    roleName,
    isDynamicDepartment
  };
}
